import Box from "@mui/material/Box";
import { useEffect, useState } from "react";

import { money } from "@/libs/tournamentSetupApi";
import type { EventPatch, FormatKey, SetupEventView, TournamentSetupView } from "@/libs/tournamentSetupApi";
import type { TournamentTermKey } from "@bw/shared/tournaments";

import { LockedField } from "../locked-field";
import { formatDay } from "../setup/fields";
import { TermHelp } from "../setup/term-help";
import { DenseButton, DenseInput, DenseSelect, FieldLabel, MONO_FONT, NewBadge, Note, SetupCard, STAFF_FONT, tone } from "../setup/ui";
import { useSetupCommand } from "./use-setup-command";

const PRIMARY: FormatKey[] = ["knockout", "knockout_consolation", "groups_knockout", "round_robin"];
const TERM: Record<FormatKey, TournamentTermKey> = {
  knockout: "knockout",
  knockout_consolation: "consolation",
  groups_knockout: "groups_knockout",
  round_robin: "round_robin",
  double_elimination: "double_elimination",
  places_playout: "places_playout",
  guaranteed_minimum: "guaranteed_matches",
};

function feeText(value: string): string {
  return `$${Number.parseFloat(value || "0").toFixed(2)}`;
}

/** One event: entry, fee, limit, days, expected entries, format list with
 * court-hours and guarantees, fit note and entry rules (F2). Each change sends
 * update_events for this event; locked fields show the server's reason. */
export function EventPanel({
  view,
  event,
  onViewChange,
  onMoreOptions,
}: {
  view: TournamentSetupView;
  event: SetupEventView;
  onViewChange: (next: TournamentSetupView) => void;
  onMoreOptions: () => void;
}) {
  const { pending, error, run } = useSetupCommand(view, onViewChange);
  const [fee, setFee] = useState(feeText(String(event.entry_fee)));
  const [limit, setLimit] = useState(String(event.entry_limit));
  const [expected, setExpected] = useState(event.expected_entries ? String(event.expected_entries) : "");
  const [moreFormats, setMoreFormats] = useState(!PRIMARY.includes(event.format));
  useEffect(() => {
    setFee(feeText(String(event.entry_fee)));
    setLimit(String(event.entry_limit));
    setExpected(event.expected_entries ? String(event.expected_entries) : "");
  }, [event]);
  const patch = (body: EventPatch) => run({ command: "update_events", event_ids: [event.event_id], patch: body } as never);
  const unit = event.entry_unit;
  const locks = event.locks as Record<string, { reason: string; alternative?: string | null }>;
  const multiStage = event.format === "groups_knockout";
  const dayOptions = view.days.map((day) => (
    <option key={day.play_date} value={day.play_date}>
      {formatDay(day.play_date)}
    </option>
  ));
  const groupsDay = event.stage_days.find((s) => s.stage === "groups")?.play_date ?? event.stage_days[0].play_date;
  const knockoutDay = event.stage_days.find((s) => s.stage === "knockout")?.play_date ?? event.stage_days[0].play_date;
  const options = event.format_options.filter((option) => PRIMARY.includes(option.format) || moreFormats);

  return (
    <SetupCard
      actions={
        <>
          <DenseButton disabled={pending} onClick={() => run({ command: "duplicate_event", event_id: event.event_id } as never)} size="sm">
            Duplicate
          </DenseButton>
          <DenseButton
            disabled={pending || !event.allowed_actions.includes("remove")}
            onClick={() => run({ command: "remove_event", event_id: event.event_id } as never)}
            size="sm"
            title={event.allowed_actions.includes("remove") ? undefined : "Players have entered this event"}
          >
            Remove
          </DenseButton>
        </>
      }
      headerHeight={44}
      sx={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
      title={event.name}
    >
      <Box sx={{ p: "10px 14px", display: "flex", flexDirection: "column", gap: "9px", overflowY: "auto" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "1.2fr 1fr 1fr" }, gap: "10px" }}>
          <Box>
            <FieldLabel id="etype">Entry type</FieldLabel>
            {/* One option until team entries arrive: shown as text, not a dead control. */}
            <Box aria-labelledby="etype" role="note" sx={{ height: 36, display: "flex", alignItems: "center", fontSize: 13.5, color: tone.ink2 }}>
              Players and pairs
            </Box>
          </Box>
          {locks.entry_fee ? (
            <LockedField alternative={locks.entry_fee.alternative} id="efee" label="Fee per entry" reason={locks.entry_fee.reason} value={fee} />
          ) : (
            <Box>
              <FieldLabel htmlFor="efee">
                Fee per entry{" "}
                {unit === "pairs" ? (
                  <Box component="span" sx={{ fontWeight: 400, color: tone.subtle }}>
                    (one fee per pair)
                  </Box>
                ) : null}
              </FieldLabel>
              <DenseInput
                disabled={pending}
                id="efee"
                inputMode="decimal"
                mono
                onBlur={() => {
                  const value = Number.parseFloat(fee.replace(/[^0-9.]/g, ""));
                  if (Number.isFinite(value) && value.toFixed(2) !== Number.parseFloat(String(event.entry_fee)).toFixed(2)) {
                    void patch({ entry_fee: money(value.toFixed(2)) });
                  }
                }}
                onChange={(e) => setFee(e.target.value)}
                value={fee}
              />
            </Box>
          )}
          <Box>
            <FieldLabel htmlFor="ecap">Entry limit</FieldLabel>
            <Box sx={{ position: "relative" }}>
              <DenseInput
                aria-describedby={event.entry_limit_floor ? "ecap-floor" : undefined}
                disabled={pending}
                id="ecap"
                inputMode="numeric"
                onBlur={() => {
                  const value = Number.parseInt(limit, 10);
                  if (Number.isFinite(value) && value !== event.entry_limit) void patch({ entry_limit: value });
                }}
                onChange={(e) => setLimit(e.target.value.replace(/[^0-9]/g, ""))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
                sx={{ pr: "52px" }}
                value={limit}
              />
              <Box component="span" sx={{ position: "absolute", right: 10, top: 8, color: tone.subtle, fontSize: 13 }}>
                {unit}
              </Box>
            </Box>
            {event.entry_limit_floor ? (
              <Box id="ecap-floor" sx={{ fontSize: 11.5, color: tone.subtle, mt: "3px" }}>
                {locks.entry_limit_floor?.reason ?? `Can’t go below ${event.entry_limit_floor}`}
              </Box>
            ) : null}
          </Box>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: multiStage ? "1fr 1fr 1.25fr" : "1fr 1.25fr" }, gap: "10px", alignItems: "end" }}>
          {multiStage ? (
            <>
              <Box>
                <FieldLabel htmlFor="d1">Groups play on</FieldLabel>
                <DenseSelect
                  disabled={pending}
                  id="d1"
                  onChange={(e) => void patch({ stage_days: [{ stage: "groups", play_date: e.target.value }, { stage: "knockout", play_date: knockoutDay }] })}
                  value={groupsDay}
                >
                  {dayOptions}
                </DenseSelect>
              </Box>
              <Box>
                <FieldLabel htmlFor="d2">Knockout plays on</FieldLabel>
                <DenseSelect
                  disabled={pending}
                  id="d2"
                  onChange={(e) => void patch({ stage_days: [{ stage: "groups", play_date: groupsDay }, { stage: "knockout", play_date: e.target.value }] })}
                  value={knockoutDay}
                >
                  {dayOptions}
                </DenseSelect>
              </Box>
            </>
          ) : (
            <Box>
              <FieldLabel htmlFor="d1">Plays on</FieldLabel>
              <DenseSelect disabled={pending} id="d1" onChange={(e) => void patch({ stage_days: [{ stage: "all", play_date: e.target.value }] })} value={event.stage_days[0].play_date}>
                {dayOptions}
              </DenseSelect>
            </Box>
          )}
          <Box>
            <FieldLabel htmlFor="eexp">
              Expecting about <NewBadge />
            </FieldLabel>
            <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <DenseInput
                disabled={pending}
                id="eexp"
                inputMode="numeric"
                mono
                onBlur={() => {
                  const value = expected ? Number.parseInt(expected, 10) : null;
                  if (value !== (event.expected_entries ?? null)) void patch({ expected_entries: value });
                }}
                onChange={(e) => setExpected(e.target.value.replace(/[^0-9]/g, ""))}
                sx={{ width: 56 }}
                value={expected}
              />
              <Box component="span" sx={{ fontSize: 11.5, color: tone.subtle, lineHeight: 1.25 }}>
                {unit}. You enter it; past tournaments fill it in once you have one.
              </Box>
            </Box>
          </Box>
        </Box>
        <Box>
          <FieldLabel id="fmt-l">
            Format{" "}
            <Box component="span" sx={{ fontWeight: 400, color: tone.subtle }}>
              Players see this before they enter.
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Box component="span" sx={{ fontWeight: 400, color: tone.subtle }}>
              Court-hours if full ({event.entry_limit} {unit})
            </Box>
          </FieldLabel>
          {locks.format ? (
            <Note sx={{ mb: "6px" }} tone="neutral">
              {locks.format.reason}
            </Note>
          ) : null}
          <Box aria-labelledby="fmt-l" role="radiogroup" sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            {options.map((option) => {
              const on = option.format === event.format;
              const disabled = !option.available || Boolean(locks.format) || pending;
              return (
                <Box
                  key={option.format}
                  sx={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    p: "6px 10px",
                    border: `1px solid ${on ? tone.brand : tone.line}`,
                    borderRadius: "8px",
                    bgcolor: on ? tone.brandNote : tone.surface,
                    boxShadow: on ? `0 0 0 3px ${tone.brandRing}` : "none",
                    minHeight: 40,
                    boxSizing: "border-box",
                    opacity: option.available ? 1 : 0.55,
                  }}
                >
                  <input
                    checked={on}
                    disabled={disabled}
                    id={`f-${option.format}`}
                    name="event-format"
                    onChange={() => void patch({ format: option.format })}
                    style={{ width: 16, height: 16, accentColor: tone.brand, margin: 0, flex: "none" }}
                    type="radio"
                  />
                  <Box component="label" htmlFor={`f-${option.format}`} sx={{ flex: 1, minWidth: 0, cursor: disabled ? "default" : "pointer" }}>
                    <Box component="b" sx={{ fontSize: 13 }}>
                      {option.label}
                    </Box>
                    <Box component="span" sx={{ display: "block", fontSize: 11.5, color: tone.subtle, lineHeight: 1.3 }}>
                      {option.description}
                    </Box>
                  </Box>
                  <TermHelp label={`About ${option.label.toLowerCase()}`} term={TERM[option.format]} />
                  <Box component="span" sx={{ font: `700 11px ${MONO_FONT}`, letterSpacing: "-.3px", color: tone.ink2, whiteSpace: "pre-line", textAlign: "right", width: 160, flex: "none", lineHeight: 1.35 }}>
                    {option.available
                      ? `${option.guarantee_label} · ${option.draws_label}\n${option.court_hours_label}`
                      : option.unavailable_reason}
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mt: "6px" }}>
            <DenseButton aria-expanded={moreFormats} onClick={() => setMoreFormats(!moreFormats)} size="sm">
              {moreFormats ? "Fewer formats ▴" : "More formats ▾"}
            </DenseButton>
            {!moreFormats ? (
              <Box component="span" sx={{ fontSize: 12, color: tone.subtle }}>
                Double elimination · Places playout · Guaranteed minimum matches
              </Box>
            ) : null}
          </Box>
        </Box>
        {event.shape_lines.length ? (
          <Box sx={{ fontSize: 12.5, color: tone.ink2, lineHeight: 1.45, p: "7px 10px", bgcolor: tone.page, borderRadius: "8px", border: `1px solid ${tone.divider}` }}>
            {event.shape_lines.map((line) => (
              <Box key={line.title}>
                <b>{line.title}</b> {line.detail}
              </Box>
            ))}
          </Box>
        ) : null}
        {event.fit_note ? <Note tone={event.fit_band === "wont_fit" ? "red" : "amber"}>{event.fit_note}</Note> : null}
        {error ? (
          <Note role="alert" tone="red">
            {error}
          </Note>
        ) : null}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, pt: 1, borderTop: `1px solid ${tone.divider}` }}>
          <Box sx={{ fontSize: 12, color: tone.muted, lineHeight: 1.45, fontFamily: STAFF_FONT }}>
            <Box component="b" sx={{ color: tone.ink }}>
              Entry rules
            </Box>{" "}
            · {event.rules_summary}
          </Box>
          <DenseButton onClick={onMoreOptions} size="sm" sx={{ flex: "none" }}>
            More options
          </DenseButton>
        </Box>
      </Box>
    </SetupCard>
  );
}
